import { CompletionSource } from '@codemirror/autocomplete'
import { AddRounded, ContentCopyRounded, DeleteRounded } from '@mui/icons-material'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material'
import { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { INode } from '../../../../../definitions/Nodes'
import CustomDataRow from './CustomDataRow'

interface P {
    data: Record<string, any>
    setSelected: React.Dispatch<React.SetStateAction<INode<'var_variable'>>>
    visibleIndices: number[]
    myCompletions: CompletionSource
    disable: boolean
    setSearch: (term: string) => void
}

const CustomDataList = ({ data, setSelected, visibleIndices, myCompletions, disable, setSearch }: P) => {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [menuRow, setMenuRow] = useState<number | null>(null)

    const value = useMemo(() => {
        if (!data) return []
        return Object.entries(data)
    }, [data])

    const rowsToRender = !visibleIndices ? value.map((entry, index) => ({ entry, index })) : visibleIndices.map(index => ({ entry: value[index], index }))

    const isLastRowEmpty = useMemo(() => {
        if (value.length === 0) return false
        const [lastKey, lastVal] = value[value.length - 1]
        return lastKey === '' && (lastVal === '' || lastVal === undefined || lastVal === null)
    }, [value])

    const updateCustomData = useCallback((newValue: [string, any][]) => {
        setSelected(prev => ({
            ...prev,
            data: {
                ...prev.data,
                item: {
                    ...prev.data.item,
                    customData: Object.fromEntries(newValue)
                }
            }
        }))
    }, [setSelected, data])

    const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>, rowIdx: number) => {
        setMenuAnchor(event.currentTarget)
        setMenuRow(rowIdx)
    }, [])

    const handleCloseMenu = useCallback(() => {
        setMenuAnchor(null)
        setMenuRow(null)
    }, [])

    const handleKeyChange = useCallback((rowIdx: number, newKey: string) => {
        const newValue = [...value]
        newValue[rowIdx] = [newKey, newValue[rowIdx][1]]
        updateCustomData(newValue)
    }, [value, updateCustomData])

    const handleValueChange = useCallback((rowIdx: number, newVal: string) => {
        const newValue = [...value]
        newValue[rowIdx] = [newValue[rowIdx][0], newVal]
        updateCustomData(newValue)
    }, [value, updateCustomData])

    const handleAddRow = useCallback(() => {
        const newValue = [...value]
        const existingKeys = new Set(newValue.map(([k]) => k))

        let counter = 1
        let newKey = `key_${counter}`
        while (existingKeys.has(newKey)) {
            counter++
            newKey = `key_${counter}`
        }

        newValue.push([newKey, ''])
        updateCustomData(newValue)
        setSearch('')
    }, [value, updateCustomData, setSearch])

    const handleDuplicate = useCallback(() => {
        if (menuRow !== null) {
            const newValue = structuredClone(value)
            const [origKey, origVal] = newValue[menuRow]

            const existingKeys = new Set(newValue.map(([k]) => k))
            let counter = 1
            const baseKey = origKey || 'key'
            let newKey = `${baseKey}_${counter}`

            while (existingKeys.has(newKey)) {
                counter++
                newKey = `${baseKey}_${counter}`
            }

            newValue.splice(menuRow + 1, 0, [newKey, origVal])
            updateCustomData(newValue)
        }
        handleCloseMenu()
    }, [menuRow, value, updateCustomData, handleCloseMenu])

    const handleDelete = useCallback(() => {
        if (menuRow !== null) {
            const newValue = [...value]
            newValue.splice(menuRow, 1)
            updateCustomData(newValue)
        }
        handleCloseMenu()
    }, [menuRow, value, updateCustomData, handleCloseMenu])

    return (
        <Stack spacing={2}>
            <Stack spacing={1}>
                {rowsToRender.map(({ entry: [k, v], index }) => (
                    <CustomDataRow
                        key={index}
                        index={index}
                        fieldKey={k}
                        fieldValue={v}
                        onKeyChange={handleKeyChange}
                        onValueChange={handleValueChange}
                        onMenuOpen={handleOpenMenu}
                        myCompletions={myCompletions}
                        disable={disable}
                    />
                ))}
            </Stack>

            <Button
                startIcon={<AddRounded />}
                onClick={handleAddRow}
                variant='text'
                size='small'
                disabled={disable || isLastRowEmpty}>
                Add Row
            </Button>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleDuplicate}>
                    <ListItemIcon>
                        <ContentCopyRounded fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                        <DeleteRounded color='error' fontSize='small' />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'var(--color-bad)' }}>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </Stack>
    )
}

export default memo(CustomDataList)
